'use client'

import { createContext, ReactNode, useContext, useMemo } from 'react'
import { ensureMbmUserId, readMbmUserIdFromDocument } from '@/utils/mbmUserId'

type MbmUserContextValue = {
  userId: string | null
}

const MbmUserContext = createContext<MbmUserContextValue>({ userId: null })

type MbmUserProviderProps = {
  children: ReactNode
}

/**
 * Ensures every browser session has an `mbm-user-id` before children render logs.
 * Falls back to a client-written cookie when edge proxy did not set one.
 */
export function MbmUserProvider({ children }: MbmUserProviderProps) {
  const userId = useMemo(() => {
    if (typeof window === 'undefined') {
      return null
    }

    return readMbmUserIdFromDocument() ?? ensureMbmUserId()
  }, [])

  return <MbmUserContext.Provider value={{ userId }}>{children}</MbmUserContext.Provider>
}

/**
 * Returns the anonymous MBM user id for the current browser session.
 */
export function useMbmUserId(): string | null {
  return useContext(MbmUserContext).userId
}
